"use client";

import { useEffect, useState } from "react";
import { APISearchSelect, APISearchSelectContent, APISearchSelectTrigger } from "@/components/ui";
import Button from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Tooltip from "@/components/ui/tooltip";
import { queryClient } from "@/config/react-query";
import { useEventTitle } from "@/hooks/use-event-title";
import { formatDate } from "@/lib/format-data";
import { toast } from "@/lib/use-toast";
import { AssistantDetail } from "@/types/assistants/assistants";
import { CallAssistantType, CallSummary, MakeAICall } from "@/types/call";
import { AssistantType } from "@/types/enums/assistant-type";
import { EventDetail } from "@/types/event";
import { fetchAssistants as apiFetchAssistants } from "@/api/provider/assistants/assistants";
import { makeAICall } from "@/api/provider/comms/call";
import { eventQueryOptions } from "@/query/check-in/calendar/event-query";
interface AICallConfigProps {
  patientId: string;
  onCallComplete?: (call: CallSummary) => void;
  onClose?: () => void;
}
export default function AICallConfig({
  patientId,
  onCallComplete,
  onClose
}: AICallConfigProps) {
  const getEventTitle = useEventTitle();
  const [assistants, setAssistants] = useState<AssistantDetail[]>([]);
  const [loadingAssistants, setLoadingAssistants] = useState(true);
  const [callParams, setCallParams] = useState<MakeAICall>({
    assistantType: "CALL_REMINDER",
    patientId,
    reasonForCall: "",
    eventId: undefined
  });
  const handleEventSelect = (event?: EventDetail) => {
    setCallParams({
      ...callParams,
      eventId: event?.id
    });
  };
  const [isCalling, setIsCalling] = useState(false);
  const makeCall = async () => {
    setIsCalling(true);
    try {
      const call = await makeAICall(callParams);
      if (call) {
        onCallComplete?.(call);
        onClose?.();
      }
    } catch {
      toast({
        title: "AI Call Error",
        description: "Unable to start call. Please try again.",
        variant: "error"
      });
      setIsCalling(false);
    }
  };
  const searchEvents = async (searchTerm: string) => {
    const events = await queryClient.fetchQuery(eventQueryOptions.searchPatientEvents(patientId, searchTerm));
    return events ?? [];
  };
  useEffect(() => {
    const fetchAssistants = async () => {
      setLoadingAssistants(true);
      try {
        const assistants_0 = await apiFetchAssistants([AssistantType.CALL_REMINDER, AssistantType.BILLER, AssistantType.CALL_FOLLOW_UP, AssistantType.CALL_LAB_TECHNICIAN]);
        if (assistants_0) {
          setAssistants(assistants_0);
        }
      } catch {
        toast({
          title: "Failed to fetch assistants",
          description: "Please try again later",
          variant: "error"
        });
      } finally {
        setLoadingAssistants(false);
      }
    };
    fetchAssistants();
  }, []);
  return <div className="space-y-4">
      <div>
        <label className="block text-body-strong text-foreground-secondary">
          Call Type
        </label>
        <Select value={callParams.assistantType} onValueChange={(value: string) => {
        setCallParams({
          ...callParams,
          assistantType: value as CallAssistantType
        });
      }} disabled={loadingAssistants}>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select call type" />
          </SelectTrigger>
          <SelectContent>
            {loadingAssistants ? <SelectItem key="loading" value="loading">
                Loading assistants...
              </SelectItem> : assistants.map(assistant => <SelectItem key={assistant.id} value={assistant.assistantType}>
                  {assistant.firstName}
                </SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-body-strong text-foreground-secondary">
          Related Event (Optional)
        </label>
        <APISearchSelect<EventDetail> onValueChange={handleEventSelect} onSearch={searchEvents} formatOption={(event_0: EventDetail) => {
        const eventTitle = getEventTitle(event_0) || "Untitled Event";
        return {
          id: event_0.id,
          name: eventTitle,
          optionNode: <div>
                  <div className="font-medium">{eventTitle}</div>
                  <div className="text-body text-muted-foreground">
                    {formatDate(event_0.start)}
                  </div>
                </div>
        };
      }}>
          <APISearchSelectTrigger placeholder="Search for related events by title or date, if applicable" />
          <APISearchSelectContent emptyMessage="No events found" />
        </APISearchSelect>
      </div>

      <div>
        <label className="flex items-center gap-1 text-body-strong text-foreground-secondary">
          Reason for Call
          <Tooltip content="Provide detailed context for the AI Agent. The more specific information you include, the better the call outcome will be."></Tooltip>
        </label>
        <textarea className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-foreground shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" rows={3} value={callParams.reasonForCall} onChange={e => {
        setCallParams({
          ...callParams,
          reasonForCall: e.target.value
        });
      }} placeholder="Enter the reason for this call, this helps the AI Agent be more helpful to the patient, the more details the better!" />
      </div>

      <Button variant="primary" disabled={isCalling || !callParams.assistantType} onClick={makeCall} className="mt-4 w-full">
        {isCalling ? "Calling..." : "Confirm AI Call"}
      </Button>
    </div>;
}